import React from "react";
import { toastManager } from "@salestrak/ui";

import { createDialogEmitter } from "#app/lib/dialog-emitter";
import { IntegrationsManageDialog } from "#app/components/integrations/integrations-manage-dialog";
import { takeOAuthReturn, type OAuthReturnOutcome } from "#app/lib/oauth-return";
import { AnalyticsContext, IntegrationEvent, trackEvent } from "#app/lib/analytics";

type IntegrationsDialogActions = {
  openDialog: () => void;
  closeDialog: () => void;
};

const integrationsDialog = createDialogEmitter("integrations");

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useIntegrationsDialogActions(): IntegrationsDialogActions {
  return React.useMemo<IntegrationsDialogActions>(
    () => ({
      openDialog: integrationsDialog.open,
      closeDialog: integrationsDialog.close,
    }),
    [],
  );
}

// How each OAuth outcome reads to the admin who just came back from the consent
// screen. Keyed by `OAuthReturnOutcome` (lib/oauth-return.ts), which mirrors
// `OAuthOutcome` in apps/api/core/src/integrations/oauth.
const OAUTH_RETURN_TOAST: Record<
  OAuthReturnOutcome,
  { title: string; type: "success" | "error" }
> = {
  connected: { title: "Integration connected", type: "success" },
  reconnected: { title: "Integration reconnected", type: "success" },
  denied: { title: "Authorization cancelled", type: "error" },
  failed: { title: "Connection failed", type: "error" },
};

export function IntegrationsDialog() {
  const { open } = integrationsDialog.useDialogState();

  // Landing back from an OAuth authorization. The flow is a full-page redirect,
  // so there is no live component waiting on a promise.
  //
  // The outcome is NOT read from the URL here. It was captured at app entry
  // (`captureOAuthReturn`, called from main.tsx) because core redirects to the
  // app root and the root's `<Navigate>` drops search params before this
  // component — mounted only under `/$workspace` — ever renders. See
  // lib/oauth-return.ts for the full routing reason.
  //
  // Handled in THIS component because it already owns "is the hub open", and
  // reopening the hub is most of the response.
  React.useEffect(() => {
    // `takeOAuthReturn` clears as it reads, so a remount (StrictMode
    // double-invokes effects in development) cannot toast the same
    // authorization twice.
    const result = takeOAuthReturn();
    if (!result) return;

    trackEvent({
      context: AnalyticsContext.Integration,
      event: IntegrationEvent.ConnectFinish,
      data: { provider: result.provider, method: "oauth", outcome: result.outcome },
    });
    toastManager.add(OAUTH_RETURN_TOAST[result.outcome]);

    // Only reopen the hub when there is something to look at or fix. A cancelled
    // authorization is a deliberate "no" — the toast is the whole response.
    if (result.outcome !== "denied") integrationsDialog.open();
  }, []);

  return <IntegrationsManageDialog open={open} onClose={integrationsDialog.close} />;
}
